import {
  EinheitenArt,
  SpartenType,
  TaasRsData,
  TaasVerkehrBaustein,
  TaasVermieterBaustein,
} from '@ps20/bff-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import {
  convertRsTaasBausteinToTarifrisikoschluessel,
  getBeamtenKennung,
  getBeruflicheStellung,
  VermietetesObjekt,
  VermietetesObjektEnum,
  VersicherterBereichEnum,
} from '@ps20-frontend/frontend-models';
import { IAppState } from '../../appState';
import { Fahrzeug } from '../../models/vertragsoptionen.model';

const AUSLAENDISCHE_ADRESSE_PLZ_RISIKOORT = '99999';
export function buildTaasRSDataForStrecke(state: IAppState): TaasRsData {
  const rsData: TaasRsData = generateBasicTaasRSData(state);

  rsData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertRsTaasBausteinToTarifrisikoschluessel,
  );
  return rsData;
}

export function buildTaasRsDataForShop(state: IAppState): TaasRsData {
  const rsData: TaasRsData = generateBasicTaasRSData(state);

  rsData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertRsTaasBausteinToTarifrisikoschluessel,
  );
  return rsData;
}

function generateVermieterBausteine(
  vermieteteObjekte: VermietetesObjekt[],
): Array<TaasVermieterBaustein> {
  return (
    vermieteteObjekte &&
    vermieteteObjekte.reduce((bausteinArray, vermieteteObjekt) => {
      const plzRisikoort = vermieteteObjekt.auslaendischeAdresse
        ? AUSLAENDISCHE_ADRESSE_PLZ_RISIKOORT
        : vermieteteObjekt.plz;

      if (vermieteteObjekt.grundstueckBebaut === VermietetesObjektEnum.BEBAUT) {
        if (vermieteteObjekt.wohneinheiten) {
          bausteinArray.push({
            plzRisikoort: plzRisikoort,
            einheitenArt: EinheitenArt.WOHNEINHEIT,
            anzahlVermieteteEinheiten: vermieteteObjekt.wohneinheiten,
            jahresBruttoMiete: vermieteteObjekt.bruttojahresmieteWohneinheiten,
            unbebauteFlaeche: 0,
            deltafaktor: vermieteteObjekt.deltafaktorWohnung,
            plzFaktor: vermieteteObjekt.plzFaktorWohnung,
            instanzId: vermieteteObjekt.instanzIdWohnung,
            auslaendischeAdresse: vermieteteObjekt.auslaendischeAdresse,
          });
        }
        if (vermieteteObjekt.gewerbeeinheiten) {
          bausteinArray.push({
            plzRisikoort: plzRisikoort,
            einheitenArt: EinheitenArt.GEWERBEEINHEIT,
            anzahlVermieteteEinheiten: vermieteteObjekt.gewerbeeinheiten,
            jahresBruttoMiete: vermieteteObjekt.bruttojahresmieteGewerbeeinheiten,
            unbebauteFlaeche: 0,
            deltafaktor: vermieteteObjekt.deltafaktorGewerbe,
            plzFaktor: vermieteteObjekt.plzFaktorGewerbe,
            instanzId: vermieteteObjekt.instanzIdGewerbe,
            auslaendischeAdresse: vermieteteObjekt.auslaendischeAdresse,
          });
        }
        if (vermieteteObjekt.stellplaetze) {
          bausteinArray.push({
            plzRisikoort: plzRisikoort,
            einheitenArt: EinheitenArt.GARAGE,
            anzahlVermieteteEinheiten: vermieteteObjekt.stellplaetze,
            jahresBruttoMiete: vermieteteObjekt.bruttojahresmieteStellplaetze,
            unbebauteFlaeche: 0,
            deltafaktor: vermieteteObjekt.deltafaktorGarage,
            plzFaktor: vermieteteObjekt.plzFaktorGarage,
            instanzId: vermieteteObjekt.instanzIdGarage,
            auslaendischeAdresse: vermieteteObjekt.auslaendischeAdresse,
          });
        }
      }

      if (vermieteteObjekt.grundstueckBebaut === VermietetesObjektEnum.UNBEBAUT) {
        bausteinArray.push({
          plzRisikoort: plzRisikoort,
          einheitenArt: EinheitenArt.UNBEBAUTES_GRUND_STUECK,
          anzahlVermieteteEinheiten: 1,
          jahresBruttoMiete: 0,
          unbebauteFlaeche: vermieteteObjekt.grundstueckFlaeche,
          deltafaktor: vermieteteObjekt.deltafaktorUnbebaut,
          plzFaktor: vermieteteObjekt.plzFaktorUnbebaut,
          instanzId: vermieteteObjekt.instanzIdUnbebaut,
          auslaendischeAdresse: vermieteteObjekt.auslaendischeAdresse,
        });
      }
      return bausteinArray;
    }, [] as Array<TaasVermieterBaustein>)
  );
}

function generateVerkehrBaustein(fahrzeug: Fahrzeug): TaasVerkehrBaustein {
  return (
    fahrzeug && {
      fahrzeugeArt: fahrzeug.fahrzeugArt,
      fahrzeugeVersichert: fahrzeug.anzahlFahrzeug,
    }
  );
}

export function generateBasicTaasRSData(state: IAppState): TaasRsData {
  const versicherterBereich = state.vertragsoptionen.versicherterBereich;
  const versicherterBereichMapping: Record<VersicherterBereichEnum, { vermieterBausteine: TaasVermieterBaustein[] | null; verkehrBaustein: TaasVerkehrBaustein | null }> = {
    [VersicherterBereichEnum.KOMBI]: {
      vermieterBausteine: generateVermieterBausteine(state.vermieteteObjekte),
      verkehrBaustein: null,
    },
    [VersicherterBereichEnum.VERKEHR]: {
      verkehrBaustein: generateVerkehrBaustein(state.vertragsoptionen.fahrzeug),
      vermieterBausteine: null,
    },
  };

  const { verkehrBaustein, vermieterBausteine } = versicherterBereichMapping[versicherterBereich];
  const vertragsoptionen = state.vertragsoptionen;
  return {
    spartenType: SpartenType.RECHTSSCHUTZ,
    salesProducts: null,
    familienstand: state.versicherungsnehmer.familienstand,
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    anzVorschaeden: state.rechtsstreitigkeiten.anzahl,
    plzVN: state.risikoort.plz,
    beruflicheStellung: getBeruflicheStellung(state.selbststaendigkeit),
    beamtenKennung: getBeamtenKennung(state.imOeffentlichenDienstTaetig),
    vermieterBausteine,
    verkehrBaustein,
    urbeginndatum: vertragsoptionen?.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
  };
}
